import { Button, Flex, SimpleGrid } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { DataTableSortStatus } from "mantine-datatable";
import { useCallback, useMemo, useState } from "react";

import { useCompanyOptions } from "../../api/hooks/customer/useCompanyOptions.ts";
import { useCreateTradeInsurance } from "../../api/hooks/tradeInsurance/useCreateTradeInsurance.ts";
import { useTradeInsurances } from "../../api/hooks/tradeInsurance/useTradeInsurances.ts";
import { useUpdateTradeInsurance } from "../../api/hooks/tradeInsurance/useUpdateTradeInsurance.ts";
import SvgPlus from "../../components/Icons/Plus.tsx";
import Layout from "../../components/Layout/Layout.tsx";
import { MultiSelect } from "../../components/MultiSelect.tsx";
import { Select } from "../../components/Select.tsx";
import Table from "../../components/Table/Table.tsx";
import { SortDirection } from "../../components/Table/types.ts";
import Title from "../../components/Title/Title.tsx";
import {
  CreateTradeInsuranceInput,
  OrderBy,
  TradeInsuranceFilter,
  TradeInsuranceOrderBy,
} from "../../graphql/generated.ts";
import { useMutationNotificationWrapper } from "../../hooks/useMutationNotificationWrapper.tsx";
import {
  TradeInsurance,
  tradeInsuranceStatusOptions,
} from "../../types/tradeInsurance.ts";
import { getCompanyOptions } from "../../utils/company.ts";
import { getPagesCount } from "../../utils/pagination.ts";
import CreateTradeInsuranceModal from "./CreateTradeInsuranceModal.tsx";
import { useTradeInsuranceTableData } from "./hooks/useTradeInsuranceTableData.tsx";
import { FiltersValue } from "./types.ts";
import {
  getTradeInsuranceApiFilter,
  getTradeInsuranceApiSort,
} from "./utils.ts";

export default function TradeInsurancePage() {
  const [
    createModalOpened,
    { open: createModalOpen, close: createModalClose },
  ] = useDisclosure(false);

  const [filter, setFilter] = useState<TradeInsuranceFilter>({});
  const [orderBy, setOrderBy] = useState<TradeInsuranceOrderBy>({
    createdAt: OrderBy.Asc,
  });
  const [page, setPage] = useState<number>(1);
  const [{ data: response, fetching }, refresh] = useTradeInsurances({
    filter,
    orderBy,
    page,
  });
  const tradeInsurances = useMemo(
    () => response?.tradeInsurances.data ?? [],
    [response?.tradeInsurances.data]
  );

  const [selectedTradeInsurance, setSelectedTradeInsurance] =
    useState<TradeInsurance | null>(null);

  const [{ data: customersResponse, fetching: fetchingCustomers }] =
    useCompanyOptions();
  const customers = customersResponse?.customers.data ?? [];
  const customerOptions = getCompanyOptions(customers);

  const [, create] = useMutationNotificationWrapper(useCreateTradeInsurance(), {
    success: { message: "Trade Insurance has been successfully created." },
  });
  const [, update] = useMutationNotificationWrapper(useUpdateTradeInsurance(), {
    success: { message: "Trade Insurance has been successfully updated." },
  });

  const handleUpdate = useCallback(
    (id: number) => {
      const tradeInsurance = tradeInsurances.find((item) => item.id === id);
      if (tradeInsurance) {
        setSelectedTradeInsurance(tradeInsurance);
        createModalOpen();
      } else {
        setSelectedTradeInsurance(null);
        createModalClose();
      }
    },
    [createModalClose, createModalOpen, tradeInsurances]
  );

  const [columns, rows] = useTradeInsuranceTableData({
    data: tradeInsurances,
    onChange: handleUpdate,
  });

  const pageCount = getPagesCount(response?.tradeInsurances.total);

  const handleSortChange = useCallback(
    (sort: DataTableSortStatus) => {
      setOrderBy(getTradeInsuranceApiSort(sort));
    },
    [setOrderBy]
  );

  const handleFilterChange = useCallback(
    (key: keyof FiltersValue, value: unknown) => {
      setFilter(getTradeInsuranceApiFilter({ [key]: value }));
    },
    [setFilter]
  );

  const handlePageChange = useCallback(
    (page: number) => {
      setPage(page);
    },
    [setPage]
  );

  const handleCreateModalClose = useCallback(() => {
    setSelectedTradeInsurance(null);
    createModalClose();
  }, [createModalClose]);

  const handleSave = useCallback(
    (input: CreateTradeInsuranceInput) => {
      const handler = selectedTradeInsurance
        ? update({ id: selectedTradeInsurance.id, input })
        : create({ input });
      handleCreateModalClose();
      handler.then((data) => {
        if (!data.error) {
          refresh();
        }
      });
    },
    [create, handleCreateModalClose, refresh, selectedTradeInsurance, update]
  );

  return (
    <Layout>
      <Flex direction="column" gap={{ base: 24, sm: 20 }}>
        <Flex direction="column" gap={16}>
          <Title size="h1">Trade Insurances</Title>
          <Button
            uppercase
            variant="filledGolden"
            leftIcon={<SvgPlus />}
            onClick={createModalOpen}
          >
            Add
          </Button>
          <SimpleGrid
            cols={5}
            breakpoints={[
              { maxWidth: "sm", cols: 2 },
              { maxWidth: "lg", cols: 3 },
            ]}
            spacing={20}
            verticalSpacing={8}
          >
            <MultiSelect
              data={customerOptions}
              loading={fetchingCustomers}
              label="Customer Name"
              placeholder="Select Customer Name"
              size="m"
              searchable
              clearable
              onChange={(value: string[]) =>
                handleFilterChange("customerId", value)
              }
            />
            <Select
              size="m"
              clearable
              label="Status"
              placeholder="Select Status"
              data={tradeInsuranceStatusOptions}
              onChange={(value: string) => handleFilterChange("status", value)}
            />
          </SimpleGrid>
        </Flex>
        <Table
          loading={fetching}
          columns={columns}
          rows={rows}
          pagination={{
            pageCount: pageCount,
            page: page,
            onPageChange: handlePageChange,
          }}
          defaultSort={{
            columnAccessor: "createdAt",
            direction: SortDirection.asc,
          }}
          onSortChange={handleSortChange}
        />
        {createModalOpened && (
          <CreateTradeInsuranceModal
            data={selectedTradeInsurance}
            customers={customers}
            opened={true}
            onSubmit={handleSave}
            onClose={handleCreateModalClose}
          />
        )}
      </Flex>
    </Layout>
  );
}
